'use client';

import React from 'react';
import { useRef } from 'react';
import styles from './RingWithHome.module.css';
import { Container } from 'components/shared';
import { CustomImage as Image } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { SectionHeader } from './SectionHeader';

// Section wrapper
const Section = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({ className, children, ...props }, ref) => (
  <section
    ref={ref}
    className={className ? `${styles.section} ${className}` : styles.section}
    {...props}
  >
    {children}
  </section>
));

Section.displayName = 'Section';

// Banner image container
const BannerContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.bannerContainer}>{children}</div>
);

const BannerImageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.bannerImageWrapper}>{children}</div>
);

// Cards grid (structure from UpcomingIntegrations, but with dark theme)
const Grid = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={styles.grid} {...props}>
    {children}
  </div>
);

// Dark-themed card component (new design, not editing original)
const DarkCard = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => (
  <article className={styles.darkCard} {...props}>
    {children}
  </article>
);

const CardAsset = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardAsset}>{children}</div>
);

const CardImage = ({
  $blendMode,
  ...props
}: {
  $blendMode?: React.CSSProperties['mixBlendMode'];
} & React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img
    alt=""
    className={styles.cardImage}
    style={
      $blendMode
        ? ({ ['--blend-mode']: $blendMode } as React.CSSProperties)
        : undefined
    }
    {...props}
  />
);

const CardText = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardText}>{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className={styles.cardTitle}>{children}</h3>
);

const CardSubtitle = (
  props: React.HTMLAttributes<HTMLParagraphElement>,
) => <p className={styles.cardSubtitle} {...props} />;

type CardContent = {
  title: string;
  description: string;
  image: string;
  blendMode?: React.CSSProperties['mixBlendMode'];
};

const cardItems: CardContent[] = [
  {
    title: 'Unlock an inside-out view of your sleep',
    description:
      "Ring metrics show what's happening inside your body while Home reveals what's happening around you - together offering a complete picture of your sleep quality.",
    image: '/web_v2/uh-home-v2/home-and-ring/updated/1.png',
  },
  {
    title: 'Connect sleep stages with your environment',
    description:
      'See how temperature, humidity, CO₂, and noise influence your sleep stages with detailed graphs that reveal what truly affects your rest.',
    image: '/web_v2/uh-home-v2/home-and-ring/updated/2.png',
  },
  {
    title: 'Hear what your sleep says about recovery',
    description:
      'Detect snoring, restlessness, and early signs of fatigue or apnea through sound analysis that uncovers how well your body is truly recovering overnight.',
    image: '/web_v2/uh-home-v2/home-and-ring/updated/3.png',
  },
];

interface RingWithHomeProps {
  className?: string;
  htmlId?: string;
}

const RingWithHome = ({ className, htmlId }: RingWithHomeProps) => {
  const rootRef = useRef<HTMLElement>(null);

  return (
    <Section
      className={className}
      id={htmlId}
      ref={rootRef}
      role="region"
      aria-labelledby={htmlId ? `${htmlId}-heading` : 'ring-with-home-heading'}
    >
      <Container>
        <BannerContainer>
          <BannerImageWrapper>
            <Image
              src={'web_v2/uh-home-v2/banners/ring-home.png'}
              alt="Ultrahuman Ring with Home device"
              fill
              style={{ objectPosition: 'center' }}
            />
          </BannerImageWrapper>
        </BannerContainer>

        <SectionHeader
          title="Home × Ring, a complete view of your sleep"
          subtitle="Ultrahuman Home and Ring AIR work together through UltraSync, correlating how your environment and physiology impact your sleep and recovery."
          variant="dark"
          titleId={htmlId ? `${htmlId}-heading` : 'ring-with-home-heading'}
          marginBottom="8rem"
          showComingSoon={true}
        />

        <Grid role="list" aria-label="Ring with Home features">
          {cardItems.map((item) => (
            <DarkCard key={item.title} role="listitem">
              <CardAsset>
                <CardImage
                  src={getCompressedAssetUrl(item.image)}
                  alt={item.title}
                  $blendMode={item.blendMode}
                />
              </CardAsset>
              <CardText>
                <CardTitle>{item.title}</CardTitle>
                <CardSubtitle
                  dangerouslySetInnerHTML={{ __html: item.description }}
                />
              </CardText>
            </DarkCard>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default RingWithHome;
